import { reklamaceAPI, taskAPI, newsAPI, shiftsAPI } from './api';
import { TASKS_MINE_PATH } from '../utils/taskNavigation';

const REKLAMACE_PATH = '/reklamace';
const NEWS_PATH = '/news';

function normalizeReklamace(row) {
    const read = Boolean(row.read_at);
    return {
        id: `reklamace:${row.id}`,
        source: 'reklamace',
        sourceLabel: 'Reklamace',
        title: row.nase_znacka || 'Reklamace',
        message: row.message,
        createdAt: row.created_at,
        read,
        link: REKLAMACE_PATH,
        markRead: async () => {
            await reklamaceAPI.markNotificationsRead([row.id]);
        },
    };
}

function normalizeTaskUnread(task) {
    const label = task.vysledek || task.ukol || 'Úkol';
    return {
        id: `task-unread:${task.id}`,
        source: 'tasks',
        sourceLabel: 'Úkoly',
        title: label,
        message: 'Nový přiřazený úkol k vyřízení',
        createdAt: task.vytvoreno,
        read: false,
        link: `${TASKS_MINE_PATH}?id=${task.id}`,
        markRead: async () => {
            await taskAPI.markRead(task.id);
        },
    };
}

function normalizeTaskOverdue(task) {
    const label = task.vysledek || task.ukol || 'Úkol';
    return {
        id: `task-overdue:${task.id}`,
        source: 'tasks',
        sourceLabel: 'Úkoly',
        title: label,
        message: 'Úkol po termínu',
        createdAt: task.deadline || task.vytvoreno,
        read: false,
        link: `${TASKS_MINE_PATH}?id=${task.id}`,
        markRead: async () => {
            await taskAPI.markRead(task.id);
        },
    };
}

function normalizeTaskRead(task) {
    const label = task.vysledek || task.ukol || 'Úkol';
    return {
        id: `task-read:${task.id}`,
        source: 'tasks',
        sourceLabel: 'Úkoly',
        title: label,
        message: 'Přečtený úkol',
        createdAt: task.precteno_v || task.vytvoreno,
        read: true,
        link: `${TASKS_MINE_PATH}?id=${task.id}`,
        markRead: null,
    };
}

function normalizeVyjezd(row) {
    const read = Boolean(row.read_at);
    const mesic = (row.mesic || '').slice(0, 7);
    return {
        id: `vyjezd:${row.id}`,
        source: 'vyjezdy',
        sourceLabel: 'Směny',
        title: 'Směna na jiné prodejně',
        message: row.message,
        createdAt: row.created_at,
        read,
        link: mesic ? `/shifts?mesic=${mesic}` : '/shifts',
        markRead: read ? null : async () => {
            await shiftsAPI.markVyjezdNotificationsRead([row.id]);
        },
    };
}

function normalizeNewsUnread(row) {
    return {
        id: `news-unread:${row.id}`,
        source: 'news',
        sourceLabel: 'Novinky',
        title: row.autor_jmeno || 'Novinka',
        message: row.obsah || 'Nová novinka',
        createdAt: row.datum_vytvoreni,
        read: false,
        link: `${NEWS_PATH}#post-${row.id}`,
        markRead: async () => {
            await newsAPI.markAllRead();
        },
    };
}

function sortByDateDesc(items) {
    return [...items].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
    });
}

export async function fetchUnreadNotifications() {
    const [reklamaceRows, tasks, summary, newsSummary, vyjezdRows] = await Promise.all([
        reklamaceAPI.listUnreadNotifications(),
        taskAPI.list({ scope: 'mine', stav: 'vse', limit: 200 }),
        taskAPI.getNotificationsSummary(),
        newsAPI.getUnreadSummary(),
        shiftsAPI.listVyjezdNotifications({ unread: true }),
    ]);

    const items = [];
    const seenTaskIds = new Set();

    if (Array.isArray(reklamaceRows)) {
        reklamaceRows.forEach((row) => items.push(normalizeReklamace(row)));
    }

    if (Array.isArray(tasks)) {
        tasks.forEach((task) => {
            if (task.typ === 'prirazeny' && task.is_unread) {
                items.push(normalizeTaskUnread(task));
                seenTaskIds.add(task.id);
            }
        });
        if (summary?.success) {
            tasks.forEach((task) => {
                if (task.urgency === 'overdue' && !seenTaskIds.has(task.id)) {
                    items.push(normalizeTaskOverdue(task));
                }
            });
        }
    }

    if (Array.isArray(newsSummary?.items)) {
        newsSummary.items.forEach((row) => items.push(normalizeNewsUnread(row)));
    }

    if (Array.isArray(vyjezdRows)) {
        vyjezdRows.forEach((row) => items.push(normalizeVyjezd(row)));
    }

    return sortByDateDesc(items);
}

export async function fetchReadNotifications() {
    const [reklamaceRows, tasks, vyjezdRows] = await Promise.all([
        reklamaceAPI.listNotifications({ unread: false }),
        taskAPI.list({ scope: 'mine', stav: 'vse', limit: 100 }),
        shiftsAPI.listVyjezdNotifications({ unread: false }),
    ]);

    const items = [];

    if (Array.isArray(reklamaceRows)) {
        reklamaceRows.forEach((row) => items.push(normalizeReklamace(row)));
    }

    if (Array.isArray(tasks)) {
        tasks.forEach((task) => {
            if (task.typ === 'prirazeny' && task.precteno_v && !task.is_unread) {
                items.push(normalizeTaskRead(task));
            }
        });
    }

    if (Array.isArray(vyjezdRows)) {
        vyjezdRows.forEach((row) => items.push(normalizeVyjezd(row)));
    }

    return sortByDateDesc(items);
}

export async function fetchUnreadCount() {
    const [reklamaceRows, summary, newsSummary, vyjezdRows] = await Promise.all([
        reklamaceAPI.listUnreadNotifications(),
        taskAPI.getNotificationsSummary(),
        newsAPI.getUnreadSummary(),
        shiftsAPI.listVyjezdNotifications({ unread: true }),
    ]);
    const reklamaceCount = Array.isArray(reklamaceRows) ? reklamaceRows.length : 0;
    let taskCount = 0;
    if (summary?.success) {
        taskCount = (summary.tasks_unread || 0) + (summary.overdue_count || 0);
    }
    const newsCount = newsSummary?.unread_count || 0;
    const vyjezdCount = Array.isArray(vyjezdRows) ? vyjezdRows.length : 0;
    return reklamaceCount + taskCount + newsCount + vyjezdCount;
}

export function dispatchNotificationsRefresh() {
    window.dispatchEvent(new Event('notifications-refresh'));
    window.dispatchEvent(new Event('tasks-notifications-refresh'));
    window.dispatchEvent(new Event('reklamace-notifications-refresh'));
    window.dispatchEvent(new Event('news-unread-refresh'));
}
